import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { classSetupActions as actions } from '.';
import { authenticationActions as actions2 } from 'features/Authentication/slice';

import httpclient from 'libs/Axios/index';
import { PayloadAction } from '@reduxjs/toolkit';
import { UserSetup } from './types';
import { addPageSize, transformStudentListAsOldFormat } from 'core/utils/constants';
import { RootState } from 'controller/types';

const getLoggedInStaffId = (authData: any) =>
  Number(authData?.STAFF_ROLE?.STAFF_ID || authData?.STAFF_INFO?.[0]?.STAFF_ID || 0);

const normalizeStaffRoles = (staff: any) =>
  (staff?.STAFF_ROLES || [])
    .map((role: any) => ({
      STAFF_ID: Number(role?.StaffRole?.STAFF_ID || role?.STAFF_ID || staff?.STAFF_ID),
      STAFF_ROLE_ID: Number(role?.StaffRole?.STAFF_ROLE_ID || role?.STAFF_ROLE_ID),
      BRANCH_ROLE_ID: Number(
        role?.BranchRole?.BRANCH_ROLE_ID ||
        role?.StaffRole?.BRANCH_ROLE_ID ||
        role?.BRANCH_ROLE_ID,
      ),
      STAFF_ROLE_NAME:
        role?.BranchRole?.NAME ||
        role?.BRANCH_ROLE_NAME ||
        role?.STAFF_ROLE_NAME ||
        role?.NAME ||
        '',
    }))
    .filter((role: any) => role.STAFF_ROLE_ID && role.STAFF_ROLE_NAME);

function* refreshLoggedInStaffRoles(staffId: number) {
  const authData = yield select(
    (state: RootState) => state.authentication?.authData,
  );
  if (!staffId || getLoggedInStaffId(authData) !== Number(staffId)) return;

  try {
    const academicYearId =
      authData?.SELECTED_ACADEMIC_YEAR?.ACADEMIC_YEAR_ID ||
      authData?.CURRENT_ACADEMIC_YEAR_ID;
    const response = yield httpclient().get(
      `/data/staff/view?id=${staffId}${academicYearId ? `&academic_year_id=${academicYearId}` : ''}`,
    );
    const responseData = response?.data?.data;
    const staff = responseData?.Staff || responseData?.staff || responseData;
    const staffRoles = normalizeStaffRoles(staff);
    const activeRole = staffRoles.find(
      (role: any) =>
        Number(role.STAFF_ROLE_ID) === Number(authData?.STAFF_ROLE?.STAFF_ROLE_ID),
    );

    yield put(actions2.updateAuthData({
      STAFF_INFO: staffRoles,
      ...(activeRole ? { STAFF_ROLE: activeRole } : {}),
    }));
  } catch (error) {
    // The role mutation already succeeded, so do not report the save as failed
    // when only the follow-up auth-state refresh fails.
    console.error('Unable to refresh logged-in staff roles:', error);
  }
}


function* fetchUserSetupList(
  action: PayloadAction<{
    ID: number;
    URL: string;
    ACADEMIC_YEAR_ID?: number;
    holiday_type?: string | undefined;
    STAFF_ROLE?: string | undefined;
    STAFF_ID?: string | undefined;
    CLASS_ID?: number;
    TABLE_CALL?: boolean;
    TABLE_DATA?: { page_size?: string | number, page_index?: string | number }
    STUDENT_CLASS_STATUS?: string;
    ADMISSION_START_DATE?:string;
    ADMISSION_END_DATE?: string;
  }>,
) {
  const { ID, URL, ACADEMIC_YEAR_ID, holiday_type, STAFF_ROLE, STAFF_ID, CLASS_ID, TABLE_DATA, STUDENT_CLASS_STATUS, ADMISSION_START_DATE,  ADMISSION_END_DATE} = action.payload;

  let payload = '';

  if (URL === 'staffLeave') {
    payload = `/data/${URL}/list?academic_year_id=${ACADEMIC_YEAR_ID}&staff_role=${STAFF_ROLE}&branch_id=${ID}`;
  } else if (URL === "student") {
    payload = '/data/' + URL + '/list/' + ID + (ACADEMIC_YEAR_ID ? '?academic_year_id=' + ACADEMIC_YEAR_ID : '') + (ADMISSION_START_DATE ? '&admission_start_date=' + ADMISSION_START_DATE : '') + (ADMISSION_END_DATE ? '&admission_end_date=' + ADMISSION_END_DATE : '') + ((CLASS_ID && ACADEMIC_YEAR_ID) ? '&class_ids=' + CLASS_ID : (CLASS_ID ? '?class_ids=' + CLASS_ID : '')) + (STUDENT_CLASS_STATUS ? '&student_class_status=' + STUDENT_CLASS_STATUS : "");
  } else if (URL === "parent") {
    payload = '/data/' + URL + '/list/' + ID + (ACADEMIC_YEAR_ID ? '?academic_year_id=' + ACADEMIC_YEAR_ID : '');
  } else if (URL === "staff") {
    payload = '/data/' + URL + '/list/' + ID;
  } else if (URL === "studentDocument") {
    payload = '/data/' + URL + '/list/' + ID;
  } else if (URL === "staffLeaveBalance") {
    payload = '/data/' + URL + '/list/' + ID + (ACADEMIC_YEAR_ID ? '?academic_year_id=' + ACADEMIC_YEAR_ID : '') + (!!STAFF_ID ? '&staff_id=' + STAFF_ID : '');
  } else if (URL === "parentFeedback") {
    payload = '/data/' + URL + '/list?branch_id=' + ID + (ACADEMIC_YEAR_ID ? '&academic_year_id=' + ACADEMIC_YEAR_ID : '') + (!!STAFF_ID ? '&staff_id=' + STAFF_ID : '');
  } else if (URL === "transferStudents") {
    // payload = '/data/studentTransferCertificate/list' + '?certificate_type=TRANSFER';
    payload = '/data/studentCertificate/list/' + 0 + '?certificate_type=TRANSFER';
  } else if(URL === "studentIdCard") {
    payload = '/data/' + URL + '/list' + (ID ? '?Branch_id=' + ID : '') + (!!ACADEMIC_YEAR_ID ? '&academic_year_id=' + ACADEMIC_YEAR_ID : '') + (!!CLASS_ID ? '&class_id=' + CLASS_ID : '');
    // payload = '/data/student/list/' + ID + (ACADEMIC_YEAR_ID ? '?academic_year_id=' + ACADEMIC_YEAR_ID : '') + (ADMISSION_START_DATE ? '&admission_start_date=' + ADMISSION_START_DATE : '') + (ADMISSION_END_DATE ? '&admission_end_date=' + ADMISSION_END_DATE : '') + ((CLASS_ID && ACADEMIC_YEAR_ID) ? '&class_ids=' + CLASS_ID : (CLASS_ID ? '?class_ids=' + CLASS_ID : '')) + (STUDENT_CLASS_STATUS ? '&student_class_status=' + STUDENT_CLASS_STATUS : "");
  } else {
    payload = '/data/' + URL + '/list/' + ID + (ACADEMIC_YEAR_ID ? '?academic_year_id=' + ACADEMIC_YEAR_ID : '');
  }
  try {
    const response = yield httpclient().get(addPageSize(payload, TABLE_DATA)); // removed TABLE_DATA
    const data = response?.data?.data;

    const listData =
      URL === 'staffTasks'
        ? data?.StaffTaskList
        :( URL === "student")? transformStudentListAsOldFormat(data?.[0]?.Students || data?.StudentList) : data[Object.keys(data).at(0)?.toString() as string] || [];
        // :( URL === "student" || URL === "studentIdCard" )? transformStudentListAsOldFormat(data?.[0]?.Students || data?.StudentList) : data[Object.keys(data).at(0)?.toString() as string] || [];
    yield put(actions.fetchUserSetupListSuccess({ listData, data }));
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    yield put(actions.fetchUserSetupListFailure(errorDetails));
  }
}

function* fetchUserSetupOne(
  action: PayloadAction<{
    STUDENT_CLASS_ID?: number;
    ID: number;
    URL: string;
    START_DATE?: any;
    END_DATE?: any;
    ACADEMIC_YEAR_ID?: any;
    STAFF_ROLE?: string;
    STAFF_ID?: number;
    BRANCH_ID?: number;
    queryParamas?: any;
    CALLED_FROM?: string;
  }>,
) {
  const {
    STUDENT_CLASS_ID,
    ID,
    URL,
    ACADEMIC_YEAR_ID,
    STAFF_ID,
    START_DATE,
    END_DATE,
    STAFF_ROLE,
    BRANCH_ID,
    queryParamas,
    CALLED_FROM
  } = action.payload;
  let url = ``;
  if (['staffTasks']?.includes(URL)) {
    url = `/data/${URL}/view?staff_id=${STAFF_ID}&query=STAFF_TASK_ID=${ID}`;
  } else if (['staffLeave']?.includes(URL)) {
    url = `/data/${URL}/view?id=${ID}`;
  } else if (['summary/student']?.includes(URL)) {
    url = `/data/${URL}?student_id=${ID}&academic_year_id=${ACADEMIC_YEAR_ID}`;
  } else if (['summary/staff']?.includes(URL)) {
    url = `/data/${URL}?staff_id=${ID}&academic_year_id=${ACADEMIC_YEAR_ID}`;
  } else if (['summary/class']?.includes(URL)) {
    url = `/data/${URL}?class_id=${ID}&academic_year_id=${ACADEMIC_YEAR_ID}&branch_id=${BRANCH_ID}`;
  } else if (['userDetail']?.includes(URL)) {
    url = `/auth/${URL}/view${queryParamas}`;
  } else if (['staffLeaveBalance']?.includes(URL)) {
    url = `/data/${URL}/view?id=${ID}`;
  } else if (['transferStudents']?.includes(URL)) {
    url = `/data/transferCertificate/view?id=${ID}`;
  } else if (['student']?.includes(URL)) {
    url = `/data/${URL}/view?id=${ID}&academic_year_id=${ACADEMIC_YEAR_ID}&student_class_id=${STUDENT_CLASS_ID}`;
  } else if (['parentFeedback']?.includes(URL)) {
    url = `/data/${URL}/view?parent_feedback_id=${ID}&academic_year_id=${ACADEMIC_YEAR_ID}`;
  } else if(URL === "studentIdCard") {
    url = `/data/${URL}/view?ID=${ID}&academic_year_id=${ACADEMIC_YEAR_ID}`;
    // payload = '/data/student/list/' + ID + (ACADEMIC_YEAR_ID ? '?academic_year_id=' + ACADEMIC_YEAR_ID : '') + (ADMISSION_START_DATE ? '&admission_start_date=' + ADMISSION_START_DATE : '') + (ADMISSION_END_DATE ? '&admission_end_date=' + ADMISSION_END_DATE : '') + ((CLASS_ID && ACADEMIC_YEAR_ID) ? '&class_ids=' + CLASS_ID : (CLASS_ID ? '?class_ids=' + CLASS_ID : '')) + (STUDENT_CLASS_STATUS ? '&student_class_status=' + STUDENT_CLASS_STATUS : "");
  } else {
    url = `/data/${URL}/view?id=${ID}&academic_year_id=${ACADEMIC_YEAR_ID}`;
  }
  try {
    const response = yield httpclient().get(addPageSize(url));
    const data = response?.data?.data;
    // const listData = data[Object.keys(data).at(0)?.toString() as string];
    if (CALLED_FROM === "MODAL") {
      yield put(actions.fetchUserSetupSummaryModalSuccess(data));
    } else {
      yield put(actions.fetchUserSetupOneSuccess(data));
    }
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    if (CALLED_FROM === "MODAL") {
      yield put(actions.fetchUserSetupSummaryModalFailure(errorDetails));
    } else {
      yield put(actions.fetchUserSetupOneFailure(errorDetails));
    }
  }
}

function* createUserSetup(
  action: PayloadAction<{
    data: UserSetup;
    URL: string;
    action?: string;
    queryParams?: any;
    message: string;
    authData?: any;
    fromStudentActivate?: boolean;
    headers?: any;
  }>,
) {
  const { URL, message, queryParams, authData, fromStudentActivate,headers } = action.payload;
  let url = '';
  if (fromStudentActivate){
    url = `/data/${URL}/activate?${queryParams}`;
  } else if (['staffTasks', 'student', 'parent']?.includes(URL)) {
    url = `/data/${URL}/upsert?staff_id=${queryParams}`;
  } else if (URL === 'staffLeave' && queryParams) {
    url = `/data/${URL}/upsert?staff_role=${queryParams}`;
  } else if (['userDetail']?.includes(URL)) {
    url = `/auth/${URL}/update`
  } else {
    url = `/data/${URL}/upsert`;
  }

  try {
    if (URL !== "changePassword") {
 const isUpsert = url.includes('/upsert');

const requestConfig = isUpsert 
? { headers: headers || { 'branch-did-number': authData?.BRANCH_DID_NUMBER || "" } }
: undefined; 
      yield httpclient().post(url, action.payload.data,requestConfig);
    } else {
      yield httpclient().put(
        '/auth/changePassword',
        action.payload.data);
    }
    if (['userDetail']?.includes(URL)) {
      yield put(actions2.loggingInSuccess(authData));
    }
    if (URL === 'staff') {
      const updatedStaffId = Number((action.payload.data as any)?.[0]?.STAFF_ID || 0);
      yield call(refreshLoggedInStaffRoles, updatedStaffId);
    }
    yield put(actions.createUserSetupSuccess(action.payload));
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    yield put(actions.createUserSetupFailure(errorDetails));
  }
}

function* deleteUserSetup(
  action: PayloadAction<{
    URL: string;
    ID?: number;
    STAFF_ID?: number;
    CLASS_ID?: number;
    STAFF_ROLE_ID?: number;
    PARENT_ID?: number;
    STUDENT_ID?: number;
    INSTITUTE_ID?: number;
    BRANCH_ID?: number;
    isStudentInactive?: boolean;
    fromStudentActivate?: boolean;
  }>,
) {
  const {
    URL,
    ID,
    STAFF_ID,
    CLASS_ID,
    STAFF_ROLE_ID,
    PARENT_ID,
    STUDENT_ID,
    BRANCH_ID,
    INSTITUTE_ID,
    isStudentInactive,
    fromStudentActivate
  } = action.payload;
  let queryParam = '';
  if (!!(STAFF_ID && CLASS_ID)) {
    queryParam = `/data/${URL}/delete?staff_id=${STAFF_ID}&class_id=${CLASS_ID}`;
  } else if(fromStudentActivate){
    queryParam = `/data/student/activate?id=${ID}`
  } else if (!!STAFF_ROLE_ID) {
    queryParam = `/data/${URL}/delete?staff_role_id=${STAFF_ROLE_ID}`;
  } else if (URL === 'studentParentLink') {
    queryParam = `/data/${URL}/delete?student_id=${STUDENT_ID}&parent_id=${PARENT_ID}`;
  } else if (BRANCH_ID && INSTITUTE_ID && ID) {
    queryParam = `/data/${URL}/delete?id=${ID}&institute_id=${INSTITUTE_ID}&branch_id=${BRANCH_ID}`;
  } else if (isStudentInactive === true){
    queryParam = `/data/${URL}/delete?id=${ID}&status=INACTIVE`; 
  }  else if (['transferStudents']?.includes(URL)) {
    queryParam = `/data/studentCertificate/delete?id=${ID}`;
  } else if(URL === "studentIdCard") {
    queryParam = `/data/studentIdCard/delete?ID=${ID}`;
  }  else {
    queryParam = `/data/${URL}/delete?id=${ID}`;
  }
  try {
    const response = fromStudentActivate ? yield httpclient().post(queryParam, {}) : yield httpclient().delete(queryParam);
    const data = response?.data?.data;
    if (URL === 'staffRole' && STAFF_ID) {
      yield call(refreshLoggedInStaffRoles, Number(STAFF_ID));
    }
    yield put(actions.deleteUserSetupSuccess({studentActivate: fromStudentActivate, studentInactive: isStudentInactive}));
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    yield put(actions.deleteUserSetupFailure(errorDetails));
  }
}

export function* classSetupSaga() {
  yield all([
    takeLatest(actions.fetchUserSetupListStart.type, fetchUserSetupList),
    takeLatest(actions.fetchUserSetupOneStart.type, fetchUserSetupOne),
    takeLatest(actions.createUserSetupStart.type, createUserSetup),
    takeLatest(actions.deleteUserSetupStart.type, deleteUserSetup),
  ]);
}
